/**
 * @fileoverview Recipient chips input for mail composers (MOR-529).
 *
 * Addresses render as removable chips (matched job contacts show their
 * name), so multiple recipients stay visually distinct — typing a comma,
 * Enter, or blurring commits the current text as a chip. Backspace on an
 * empty input removes the last chip. Value is a plain `string[]`.
 */

import { useMemo, useState } from "react";
import { X } from "lucide-react";

import { cn } from "@/utils/misc";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface RecipientOption {
  email: string;
  displayName: string | null;
}

interface RecipientInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  /** Known contacts — matched chips show the contact's name. */
  contacts?: RecipientOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function RecipientInput({
  value,
  onChange,
  contacts,
  placeholder,
  disabled,
  className,
}: RecipientInputProps) {
  const [text, setText] = useState("");

  const nameByEmail = useMemo(() => {
    const map = new Map<string, string>();
    for (const contact of contacts ?? []) {
      if (contact.displayName) {
        map.set(contact.email.trim().toLowerCase(), contact.displayName);
      }
    }
    return map;
  }, [contacts]);

  const commit = (raw: string) => {
    const parts = raw
      .split(",")
      .map((a) => a.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    const existing = new Set(value.map((a) => a.toLowerCase()));
    const additions = parts.filter((a) => !existing.has(a.toLowerCase()));
    if (additions.length > 0) onChange([...value, ...additions]);
    setText("");
  };

  const removeAt = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
      if (text.trim().length > 0) {
        e.preventDefault();
        commit(text);
      } else if (e.key === ",") {
        e.preventDefault();
      }
    } else if (e.key === "Backspace" && text.length === 0 && value.length > 0) {
      e.preventDefault();
      removeAt(value.length - 1);
    }
  };

  return (
    <div
      className={cn(
        "flex min-h-7 flex-1 flex-wrap items-center gap-1",
        className,
      )}
    >
      {value.map((address, i) => {
        const name = nameByEmail.get(address.trim().toLowerCase());
        const invalid = !EMAIL_RE.test(address.trim());
        return (
          <span
            key={`${address}-${i}`}
            className={cn(
              "inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-xs",
              invalid
                ? "border-destructive/40 bg-destructive/5 text-destructive"
                : "border-border bg-muted/60 text-foreground",
            )}
            title={name ? address : undefined}
          >
            <span className="truncate">{name ?? address}</span>
            {!disabled && (
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="shrink-0 rounded-full text-muted-foreground hover:text-foreground"
                aria-label={`Remove ${address}`}
              >
                <X className="size-3" />
              </button>
            )}
          </span>
        );
      })}
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => commit(text)}
        placeholder={value.length === 0 ? placeholder : undefined}
        disabled={disabled}
        className="min-w-[140px] flex-1 bg-transparent py-0.5 text-sm outline-none placeholder:text-muted-foreground/60 disabled:cursor-not-allowed"
      />
    </div>
  );
}
